import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Flag, Users, Trophy, ArrowRight, Calendar, Medal, MapPin,
  Filter, X, Clock, Search, Newspaper, ListChecks, Archive as ArchiveIcon,
} from "lucide-react";
import { EntityTimeline } from "@/components/EntityTimeline";
import { ShareButton } from "@/components/ShareButton";
import { resolveAthletePhoto } from "@/lib/photoFallback";

const ALL = "__all__";

// ---------------------------------------------------------------------------
//  Éléments visuels réutilisables
// ---------------------------------------------------------------------------
function MedalPill({ tone, count, label }) {
  const cls = tone === "or"
    ? "bg-[hsl(var(--aito-gold))]/15 text-[hsl(var(--aito-gold))] border-[hsl(var(--aito-gold))]/30"
    : tone === "argent"
      ? "bg-slate-200/60 text-slate-700 border-slate-300 dark:bg-slate-800/60 dark:text-slate-200 dark:border-slate-700"
      : "bg-amber-200/40 text-amber-900 border-amber-300 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cls}`} data-testid={`games-medal-${tone}`}>
      <Medal className="h-3 w-3" />
      <span className="font-bold">{count}</span>
      <span className="opacity-70">{label}</span>
    </span>
  );
}

function AthleteRow({ a }) {
  // V11.8 — Toujours une photo réelle OU une silhouette fallback (jamais d'initiales).
  const photoUrl = resolveAthletePhoto(a);
  return (
    <Link to={`/athletes/${a.slug || a.athlete_id}`} className="group" data-testid={`games-athlete-${a.athlete_id}`}>
      <Card className="p-4 flex items-center gap-3 aito-card-hover h-full border-[hsl(var(--aito-gold))]/60">
        <div className="h-14 w-14 shrink-0 rounded-full overflow-hidden bg-muted ring-2 ring-[hsl(var(--aito-gold))]/40">
          <img
            src={photoUrl}
            alt={a.full_name}
            className="h-full w-full object-cover"
            onError={(e) => { e.currentTarget.src = "/branding/athlete-male.png"; }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-semibold truncate group-hover:text-[hsl(var(--aito-lagoon))]">{a.full_name}</div>
          <div className="text-xs text-muted-foreground capitalize truncate">
            {[a.discipline, a.club].filter(Boolean).join(" · ")}
          </div>
          <div className="flex flex-wrap items-center gap-1 mt-1.5">
            <Badge className="text-[9px] uppercase bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]">Sélectionné·e</Badge>
            {a.medals?.or > 0 && <MedalPill tone="or" count={a.medals.or} label="or" />}
            {a.medals?.argent > 0 && <MedalPill tone="argent" count={a.medals.argent} label="ar." />}
            {a.medals?.bronze > 0 && <MedalPill tone="bronze" count={a.medals.bronze} label="br." />}
          </div>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 group-hover:text-[hsl(var(--aito-lagoon))] transition-colors" />
      </Card>
    </Link>
  );
}

function DisciplineCard({ d, onClick, active, prefix = "" }) {
  return (
    <button onClick={onClick} className="text-left w-full" data-testid={`${prefix}discipline-${d.discipline}`}>
      <Card className={`p-3 h-full aito-card-hover transition-all ${active ? "border-[hsl(var(--aito-lagoon))] ring-2 ring-[hsl(var(--aito-lagoon))]/40" : ""}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-semibold capitalize truncate">{d.discipline}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2">
              <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" />{d.events} épreuve{d.events > 1 ? "s" : ""}</span>
              {d.events_finished > 0 && <span>· {d.events_finished} terminée{d.events_finished > 1 ? "s" : ""}</span>}
              {d.selected > 0 && <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{d.selected} PYF</span>}
            </div>
          </div>
        </div>
        {d.medals?.total > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {d.medals.or > 0 && <MedalPill tone="or" count={d.medals.or} label="or" />}
            {d.medals.argent > 0 && <MedalPill tone="argent" count={d.medals.argent} label="ar." />}
            {d.medals.bronze > 0 && <MedalPill tone="bronze" count={d.medals.bronze} label="br." />}
          </div>
        )}
      </Card>
    </button>
  );
}

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function EventRow({ event, tone = "default" }) {
  const dt = event.date ? new Date(event.date) : null;
  const now = new Date();
  const isPast = dt && dt < now;
  return (
    <div className="flex items-start gap-2 py-2 border-b border-border last:border-0" data-testid={`games-event-${event.id}`}>
      <Trophy className={`h-4 w-4 shrink-0 mt-1 ${tone === "pyf" ? "text-[hsl(var(--aito-gold))]" : isPast ? "text-muted-foreground" : "text-[hsl(var(--aito-lagoon))]"}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-muted-foreground">
          {dt && <span>{fmtDate(dt)}</span>}
          {event.time && <span>· {event.time}</span>}
          {event.discipline && <Badge variant="outline" className="text-[9px] capitalize">{event.discipline}</Badge>}
          {event.status === "finished" && <Badge variant="secondary" className="text-[9px]">terminée</Badge>}
          {event.status === "live" && <Badge className="text-[9px] bg-[hsl(var(--aito-energy))] text-white hover:bg-[hsl(var(--aito-energy))]">en cours</Badge>}
          {event.status === "scheduled" && <Badge variant="outline" className="text-[9px]">à venir</Badge>}
        </div>
        <div className="text-sm font-medium line-clamp-1">{event.name}</div>
        {event.location && (
          <div className="text-[11px] text-muted-foreground inline-flex items-center gap-0.5">
            <MapPin className="h-2.5 w-2.5" />{event.location}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultRow({ r, highlight = false }) {
  return (
    <div className={`flex items-start gap-2 py-2 border-b border-border last:border-0 ${highlight ? "bg-[hsl(var(--aito-gold))]/5" : ""}`} data-testid={`games-result-${r.id}`}>
      <span className="font-mono text-xs font-bold w-6 text-muted-foreground text-right shrink-0">{r.rank || "—"}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-muted-foreground">
          <Badge variant="outline" className="text-[9px]">{r.delegation_code}</Badge>
          {r.discipline && <Badge variant="outline" className="text-[9px] capitalize">{r.discipline}</Badge>}
          {r.medal === "or" && <MedalPill tone="or" count={1} label="Or" />}
          {r.medal === "argent" && <MedalPill tone="argent" count={1} label="Argent" />}
          {r.medal === "bronze" && <MedalPill tone="bronze" count={1} label="Bronze" />}
        </div>
        <div className="text-sm font-medium">
          {r.athlete?.slug ? (
            <Link to={`/athletes/${r.athlete.slug}`} className="hover:text-[hsl(var(--aito-lagoon))]">{r.full_name}</Link>
          ) : (
            <span>{r.full_name}</span>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground">
          {r.event?.name}
          {r.performance && <> · <span className="font-mono">{r.performance}</span></>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  V11.1 — Tableaux dynamiques Médailles (par délégation / par discipline)
// ---------------------------------------------------------------------------
function MedalRow({ label, sub, or: g = 0, argent: s = 0, bronze: b = 0, total: t = 0, highlight = false, testId }) {
  return (
    <tr className={`border-b last:border-0 ${highlight ? "bg-[hsl(var(--aito-gold))]/10 font-medium" : ""}`} data-testid={testId}>
      <td className="py-2 px-3">
        <div className="capitalize">{label}</div>
        {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
      </td>
      <td className="py-2 px-3 text-center font-mono font-bold text-[hsl(var(--aito-gold))]">{g}</td>
      <td className="py-2 px-3 text-center font-mono">{s}</td>
      <td className="py-2 px-3 text-center font-mono">{b}</td>
      <td className="py-2 px-3 text-center font-mono font-bold">{t}</td>
    </tr>
  );
}

function MedalTableByDelegation({ code, testIdPrefix = "mtbd" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/games/medal-table-by-delegation", { params: { delegation: code } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [code]);

  return (
    <Card className="overflow-hidden" data-testid={`${testIdPrefix}-table`}>
      {loading ? (
        <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : !data || data.items.length === 0 ? (
        <div className="p-4 text-xs text-muted-foreground italic">
          Aucune médaille validée pour {data?.delegation?.name || code} à ce jour.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-2 px-3 text-left">Discipline</th>
              <th className="py-2 px-3 text-center text-[hsl(var(--aito-gold))]">Or</th>
              <th className="py-2 px-3 text-center">Argent</th>
              <th className="py-2 px-3 text-center">Bronze</th>
              <th className="py-2 px-3 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <MedalRow
                key={it.discipline}
                label={it.discipline_name}
                or={it.or}
                argent={it.argent}
                bronze={it.bronze}
                total={it.total}
                testId={`${testIdPrefix}-row-${it.discipline}`}
              />
            ))}
            <MedalRow
              label="Total"
              or={data.totals.or}
              argent={data.totals.argent}
              bronze={data.totals.bronze}
              total={data.totals.total}
              highlight
              testId={`${testIdPrefix}-row-total`}
            />
          </tbody>
        </table>
      )}
    </Card>
  );
}

function MedalTableByDiscipline({ discipline, testIdPrefix = "mtbdisc" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!discipline) { setData(null); setLoading(false); return; }
    setLoading(true);
    api.get("/games/medal-table", { params: { discipline } })
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [discipline]);

  return (
    <Card className="overflow-hidden" data-testid={`${testIdPrefix}-table`}>
      {loading ? (
        <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : !data || data.items.length === 0 ? (
        <div className="p-4 text-xs text-muted-foreground italic">
          {discipline ? `Aucune médaille validée pour cette discipline à ce jour.` : `Sélectionnez une discipline pour voir la répartition par délégation.`}
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
              <th className="py-2 px-3 text-left">Délégation</th>
              <th className="py-2 px-3 text-center text-[hsl(var(--aito-gold))]">Or</th>
              <th className="py-2 px-3 text-center">Argent</th>
              <th className="py-2 px-3 text-center">Bronze</th>
              <th className="py-2 px-3 text-center">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it) => (
              <MedalRow
                key={it.code}
                label={it.name}
                sub={it.is_associate ? "Membre associé" : null}
                or={it.or}
                argent={it.argent}
                bronze={it.bronze}
                total={it.total}
                highlight={it.is_home}
                testId={`${testIdPrefix}-row-${it.code}`}
              />
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}


// ---------------------------------------------------------------------------
//  Page
// ---------------------------------------------------------------------------
export default function Road2027Page() {
  const [synth, setSynth] = useState(null);
  const [delegation, setDelegation] = useState(null);
  const [disciplines, setDisciplines] = useState([]);
  const [events, setEvents] = useState({ upcoming: [], past: [] });
  const [results, setResults] = useState([]);
  const [pyfResults, setPyfResults] = useState([]);
  const [medalTable, setMedalTable] = useState([]);
  const [delegations, setDelegations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [disciplineFilter, setDisciplineFilter] = useState(ALL);

  // V11.1 — Sélecteurs des nouveaux tableaux de médailles
  const [medalDisc, setMedalDisc] = useState(null);       // discipline pour tableau par discipline
  const [medalDeleg, setMedalDeleg] = useState("PYF");    // délégation pour tableau par délégation (défaut PYF)

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/games/synthesis").catch(() => ({ data: null })),
      api.get("/games/polynesia-delegation").catch(() => ({ data: { items: [] } })),
      api.get("/games/disciplines").catch(() => ({ data: { items: [] } })),
      api.get("/games/events", { params: { limit: 300 } }).catch(() => ({ data: { items: [] } })),
      api.get("/games/results", { params: { limit: 200 } }).catch(() => ({ data: { items: [] } })),
      api.get("/games/results", { params: { only_pyf: true, limit: 30 } }).catch(() => ({ data: { items: [] } })),
      api.get("/games/medal-table").catch(() => ({ data: { items: [] } })),
      api.get("/games/delegations").catch(() => ({ data: { items: [] } })),
    ]).then(([s, p, d, ev, rs, rsPyf, mt, dg]) => {
      setSynth(s.data);
      setDelegation(p.data);
      setDisciplines(d.data.items || []);
      const now = new Date();
      const upcoming = (ev.data.items || []).filter((e) => !e.date || new Date(e.date) >= now || e.status === "live");
      const past = (ev.data.items || []).filter((e) => e.date && new Date(e.date) < now && e.status !== "live");
      setEvents({ upcoming, past });
      setResults(rs.data.items || []);
      setPyfResults(rsPyf.data.items || []);
      setMedalTable(mt.data.items || []);
      setDelegations(dg.data.items || []);
      // Défaut : première discipline JP qui a au moins un résultat.
      const firstWithMedals = (d.data.items || []).find((x) => (x.medals?.total || 0) > 0) || (d.data.items || [])[0];
      if (firstWithMedals && !medalDisc) setMedalDisc(firstWithMedals.discipline);
    }).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // V11.9 — Date pilotée par site_settings (fallback 14 juillet 2027)
  const [siteSettings, setSiteSettings] = useState(null);
  useEffect(() => {
    api.get("/site-settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  }, []);
  const openingDateStr = siteSettings?.jp2027?.opening_date || "2027-07-14";
  const target = useMemo(() => new Date(`${openingDateStr}T00:00:00`), [openingDateStr]);
  const days = Math.max(0, Math.ceil((target - new Date()) / (1000 * 60 * 60 * 24)));

  const pyf = synth?.polynesia || { selected: 0, disciplines: 0, medals: { or: 0, argent: 0, bronze: 0, total: 0 } };

  // Nos AITO — filtrés
  const filteredDelegation = useMemo(() => {
    const items = delegation?.items || [];
    return items.filter((a) => {
      if (disciplineFilter !== ALL && a.discipline !== disciplineFilter) return false;
      const q = search.trim().toLowerCase();
      if (q && !(a.full_name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [delegation, disciplineFilter, search]);

  // Nos AITO prochainement en lice = événements 'scheduled' ou 'live' dont la discipline
  // est représentée par au moins un·e sélectionné·e PYF.
  const pyfDisciplinesSet = useMemo(() => {
    return new Set((delegation?.items || []).map((a) => a.discipline).filter(Boolean));
  }, [delegation]);
  const pyfUpcoming = useMemo(() => {
    return events.upcoming
      .filter((e) => pyfDisciplinesSet.has(e.discipline))
      .filter((e) => disciplineFilter === ALL || e.discipline === disciplineFilter)
      .slice(0, 8);
  }, [events.upcoming, pyfDisciplinesSet, disciplineFilter]);

  // Derniers résultats de nos AITO
  const pyfLatestResults = useMemo(() => {
    return pyfResults
      .filter((r) => disciplineFilter === ALL || r.discipline === disciplineFilter)
      .slice(0, 8);
  }, [pyfResults, disciplineFilter]);

  // Disciplines représentées par nos AITO
  const pyfDisciplinesCards = useMemo(() => {
    const bySlug = new Map();
    (disciplines || []).forEach((d) => bySlug.set(d.discipline, d));
    const set = pyfDisciplinesSet;
    return Array.from(set)
      .map((slug) => bySlug.get(slug) || { discipline: slug, events: 0, events_finished: 0, selected: 0, medals: { or: 0, argent: 0, bronze: 0, total: 0 } })
      .sort((a, b) => (b.medals?.total || 0) - (a.medals?.total || 0) || (b.selected || 0) - (a.selected || 0));
  }, [pyfDisciplinesSet, disciplines]);

  const globalFilteredResults = useMemo(() => {
    return results.filter((r) => disciplineFilter === ALL || r.discipline === disciplineFilter);
  }, [results, disciplineFilter]);

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-6" data-testid="games-page">
      {/* V11.10/V11.20 — Hero avec logo officiel Tahiti 2027 + lien site officiel.
          Mobile : logo au-dessus du texte (évite d'écraser). Desktop : côte à côte. */}
      <div className="rounded-2xl aito-grad-hero text-white p-5 sm:p-7 overflow-hidden relative">
        <div className="relative flex flex-col md:flex-row md:items-start md:justify-between gap-4 md:gap-5">
          {/* Colonne gauche : logo officiel + texte */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-5 min-w-0 flex-1">
            <a
              href="https://www.tahiti2027.pf"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Site officiel Jeux du Pacifique Tahiti 2027 — s'ouvre dans un nouvel onglet"
              title="Visiter le site officiel tahiti2027.pf"
              data-testid="games-official-logo-link"
              className="shrink-0 self-start inline-block rounded-lg bg-white p-2 sm:p-3 shadow-md hover:shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-gold))]"
            >
              <img
                src="/branding/jp2027-logo.png"
                alt="Logo officiel Jeux du Pacifique Tahiti 2027"
                className="h-12 sm:h-16 md:h-20 w-auto object-contain"
                loading="eager"
              />
            </a>
            <div className="min-w-0 flex-1">
              <Badge className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold))]">Officiel</Badge>
              <h1 className="font-display text-xl sm:text-2xl md:text-3xl font-bold mt-2 leading-tight">Jeux du Pacifique 2027</h1>
              <p className="mt-1 text-sm text-white/80 max-w-2xl">
                Nos AITO aux Jeux — suivez leurs épreuves, leurs résultats et les médailles de la Polynésie.
              </p>
              <a
                href="https://www.tahiti2027.pf"
                target="_blank"
                rel="noreferrer noopener"
                data-testid="games-official-site-link"
                className="inline-flex items-center gap-1.5 mt-2 text-xs font-medium text-[hsl(var(--aito-gold))] hover:text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-gold))] rounded break-all"
              >
                Site officiel : tahiti2027.pf
                <ArrowRight className="h-3 w-3 shrink-0" />
              </a>
            </div>
          </div>
          {/* Colonne droite : compteur + partage */}
          <div className="flex flex-row md:flex-col md:items-end gap-2 shrink-0 flex-wrap">
            <div className="inline-flex items-center gap-2 bg-black/40 border border-white/20 rounded-full px-3 py-1.5 text-xs" data-testid="games-countdown">
              <Clock className="h-3.5 w-3.5 text-[hsl(var(--aito-gold))]" />
              <span className="font-mono">J-{days}</span>
              <span className="opacity-70">avant les Jeux</span>
            </div>
            <ShareButton
              kind="road2027"
              slug="page"
              title="Jeux du Pacifique 2027 — AITO.PF"
              text="Suivi officiel des Jeux du Pacifique 2027."
              canonicalPath="/road-2027"
              dataTestId="games-share-button"
              variant="outline"
              size="sm"
            />
          </div>
        </div>
      </div>

      {/* V13.19 — Accès rapide à l'archive historique (page Mémoire du Sport) */}
      <Link
        to="/memoire#focus-jeux-pacifique"
        className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-[hsl(var(--aito-gold))]/40 bg-[hsl(var(--aito-gold))]/5 hover:bg-[hsl(var(--aito-gold))]/10 hover:border-[hsl(var(--aito-gold))]/60 text-sm font-medium text-[hsl(var(--aito-gold-strong,var(--aito-gold)))] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--aito-gold))]"
        data-testid="games-history-cta"
      >
        <ArchiveIcon className="h-4 w-4" />
        Voir l'historique des précédents Jeux &amp; Mini-Jeux du Pacifique
        <ArrowRight className="h-3.5 w-3.5 opacity-70" />
      </Link>

      {/* 1. KPI POLYNÉSIE (bloc unique compact) */}
      <section className="mt-5" data-testid="games-pyf-kpi">
        <Card className="p-4">
          <div className="flex items-center flex-wrap gap-x-6 gap-y-3">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground shrink-0">
              <Flag className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
              Polynésie française
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-x-6 gap-y-2 flex-1">
              <MiniStat label="AITO sélectionnés" value={loading ? "…" : pyf.selected} testId="games-pyf-selected" />
              <MiniStat label="Disciplines" value={loading ? "…" : pyf.disciplines} testId="games-pyf-disciplines" />
              <MiniStat label="Or" value={loading ? "…" : pyf.medals.or} tone="or" testId="games-pyf-medal-or" />
              <MiniStat label="Argent" value={loading ? "…" : pyf.medals.argent} tone="argent" testId="games-pyf-medal-argent" />
              <MiniStat label="Bronze" value={loading ? "…" : pyf.medals.bronze} tone="bronze" testId="games-pyf-medal-bronze" />
              <MiniStat label="Total" value={loading ? "…" : pyf.medals.total} tone="total" testId="games-pyf-medal-total" />
            </div>
          </div>
        </Card>
      </section>

      {/* Filtres globaux */}
      <Card className="mt-4 p-3" data-testid="games-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Rechercher un·e athlète…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="games-search" />
          </div>
          <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
            <SelectTrigger className="h-9 w-[200px]" data-testid="games-filter-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ALL}>Toutes disciplines</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.discipline} value={d.discipline}>{d.discipline}</SelectItem>)}
            </SelectContent>
          </Select>
          {(disciplineFilter !== ALL || search) && (
            <Button variant="ghost" size="sm" onClick={() => { setDisciplineFilter(ALL); setSearch(""); }} data-testid="games-clear-filters">
              <X className="h-3 w-3 mr-1" />Effacer
            </Button>
          )}
        </div>
      </Card>

      {/* 2. Nos AITO aux Jeux */}
      <SectionHeader icon={Flag} title="Nos AITO aux Jeux" hint={`${filteredDelegation.length} sélectionné·e·s officielle${filteredDelegation.length > 1 ? "s" : ""}`} testId="games-section-aito" />
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      ) : filteredDelegation.length === 0 ? (
        <EmptyState icon={Flag} title="Aucun·e AITO en lice pour l'instant" description="Les athlètes déclareront leur sélection depuis leur fiche ; elle apparaîtra ici une fois validée par AITO / COPF." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="games-delegation-list">
          {filteredDelegation.map((a) => <AthleteRow key={a.selection_id} a={a} />)}
        </div>
      )}

      {/* 3. V11.20 — Tableau général des médailles (déplacé ici, juste au-dessus
              de "Médailles par discipline" pour une lecture top-down cohérente). */}
      <SectionHeader icon={Trophy} title="Tableau général des médailles" testId="games-section-medal-table" />
      <Card className="overflow-x-auto" data-testid="games-medal-table">
        {loading ? (
          <div className="p-4 space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
        ) : medalTable.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground italic">Aucune médaille officielle enregistrée pour l'instant.</div>
        ) : (
          <table className="w-full text-sm min-w-[520px]">
            <thead>
              <tr className="border-b bg-muted/40 text-[11px] uppercase tracking-wide text-muted-foreground">
                <th className="py-2 px-3 text-left">#</th>
                <th className="py-2 px-3 text-left">Délégation</th>
                <th className="py-2 px-3 text-center text-[hsl(var(--aito-gold))]">Or</th>
                <th className="py-2 px-3 text-center">Argent</th>
                <th className="py-2 px-3 text-center">Bronze</th>
                <th className="py-2 px-3 text-center">Total</th>
              </tr>
            </thead>
            <tbody>
              {medalTable.map((m, i) => (
                <tr key={m.code} className={`border-b last:border-0 ${m.is_home ? "bg-[hsl(var(--aito-gold))]/10 font-medium" : ""}`} data-testid={`games-medal-table-row-${m.code}`}>
                  <td className="py-2 px-3 font-mono text-xs">{i + 1}</td>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-2">
                      {m.flag_url && <img src={m.flag_url} alt={m.code} className="h-3 w-5 object-cover rounded-sm" />}
                      <Badge variant="outline" className="text-[9px]">{m.code}</Badge>
                      {m.name}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-center font-mono font-bold text-[hsl(var(--aito-gold))]">{m.or}</td>
                  <td className="py-2 px-3 text-center font-mono">{m.argent}</td>
                  <td className="py-2 px-3 text-center font-mono">{m.bronze}</td>
                  <td className="py-2 px-3 text-center font-mono font-bold">{m.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* 4. V11.8 — Médailles par discipline (déplacé sous les athlètes en jeu,
              plus contextuel : "voici nos AITO, et voici les médailles remportées
              dans les disciplines concernées"). */}
      <SectionHeader icon={Medal} title="Médailles par discipline" testId="games-section-medals-by-discipline" />
      <Card className="p-3 mb-3" data-testid="games-medals-by-disc-selector">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Choisir une discipline</span>
          <Select value={medalDisc || ""} onValueChange={(v) => setMedalDisc(v)}>
            <SelectTrigger className="h-9 w-[240px]" data-testid="games-medals-by-disc-select"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(disciplines || []).map((d) => (
                <SelectItem key={d.discipline} value={d.discipline} className="capitalize">{d.discipline}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>
      <MedalTableByDiscipline discipline={medalDisc} testIdPrefix="games-medals-by-disc" />

      {/* 4. Derniers résultats de nos AITO */}
      <SectionHeader icon={Medal} title="Derniers résultats de nos AITO" testId="games-section-pyf-results" />
      <Card className="p-4" data-testid="games-pyf-results-list">
        {loading ? (
          <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
        ) : pyfLatestResults.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">Aucun résultat officiel pour nos AITO à ce jour.</p>
        ) : (
          pyfLatestResults.map((r) => <ResultRow key={r.id} r={r} highlight />)
        )}
      </Card>

      {/* 5. V11.8 — Retiré : "Nos AITO prochainement en lice", "Médailles de la Polynésie"
             (redondant avec KPI en haut) et "Médailles Polynésie par discipline"
             (redondant avec le bloc juste au-dessus). */}

      {/* 6. Disciplines représentées par nos AITO */}
      <SectionHeader icon={ListChecks} title="Disciplines représentées par nos AITO" testId="games-section-pyf-disciplines" />
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : pyfDisciplinesCards.length === 0 ? (
        <EmptyState icon={ListChecks} title="Aucune discipline représentée pour l'instant" description="Elles apparaîtront dès qu'un·e AITO aura une sélection officielle validée." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="games-pyf-disciplines-list">
          {pyfDisciplinesCards.map((d) => (
            <DisciplineCard
              key={d.discipline}
              d={d}
              prefix="pyf-"
              active={disciplineFilter === d.discipline}
              onClick={() => setDisciplineFilter(disciplineFilter === d.discipline ? ALL : d.discipline)}
            />
          ))}
        </div>
      )}

      {/* 7. Calendrier des Jeux */}
      <SectionHeader icon={Calendar} title="Calendrier des Jeux" testId="games-section-calendar" />
      <Tabs defaultValue="upcoming">
        <TabsList data-testid="games-calendar-tabs">
          <TabsTrigger value="upcoming" data-testid="games-calendar-upcoming">À venir ({events.upcoming.length})</TabsTrigger>
          <TabsTrigger value="past" data-testid="games-calendar-past">Passées ({events.past.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-3">
          <Card className="p-4">
            {loading ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : events.upcoming.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucune épreuve à venir programmée.</p>
            ) : (
              events.upcoming
                .filter((e) => disciplineFilter === ALL || e.discipline === disciplineFilter)
                .map((e) => <EventRow key={e.id} event={e} />)
            )}
          </Card>
        </TabsContent>
        <TabsContent value="past" className="mt-3">
          <Card className="p-4">
            {loading ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
            ) : events.past.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aucune épreuve terminée.</p>
            ) : (
              events.past
                .filter((e) => disciplineFilter === ALL || e.discipline === disciplineFilter)
                .map((e) => <EventRow key={e.id} event={e} />)
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* 8. V11.20 — "Tableau général des médailles" déplacé plus haut,
              juste au-dessus de "Médailles par discipline". */}

      {/* 8c. V11.1 — Consultation par délégation (sélecteur, autres pays) */}
      <SectionHeader icon={Flag} title="Consultation par délégation" testId="games-section-medals-by-delegation" />
      <Card className="p-3 mb-3" data-testid="games-medals-by-deleg-selector">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Choisir une délégation</span>
          <Select value={medalDeleg} onValueChange={setMedalDeleg}>
            <SelectTrigger className="h-9 w-[260px]" data-testid="games-medals-by-deleg-select"><SelectValue placeholder="Délégation" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {(delegations || []).map((dg) => (
                <SelectItem key={dg.code} value={dg.code}>
                  {dg.name}{dg.is_associate ? " (associé)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>
      <MedalTableByDelegation code={medalDeleg} testIdPrefix="games-medals-by-deleg" />
      <SectionHeader icon={ListChecks} title="Disciplines & résultats globaux" testId="games-section-global-disciplines" />
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : disciplines.length === 0 ? (
        <EmptyState icon={ListChecks} title="Aucune discipline pour l'instant" description="Elles s'afficheront ici dès que les épreuves officielles seront importées." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="games-global-disciplines-list">
          {disciplines.map((d) => (
            <DisciplineCard key={d.discipline} d={d} prefix="global-" active={disciplineFilter === d.discipline} onClick={() => setDisciplineFilter(disciplineFilter === d.discipline ? ALL : d.discipline)} />
          ))}
        </div>
      )}
      <div className="mt-4">
        <Card className="p-4" data-testid="games-global-results-list">
          <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
            Résultats officiels {disciplineFilter !== ALL && <>· <span className="capitalize">{disciplineFilter}</span></>}
          </div>
          {loading ? (
            <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
          ) : globalFilteredResults.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">Aucun résultat pour ce filtre.</p>
          ) : (
            globalFilteredResults.slice(0, 12).map((r) => <ResultRow key={r.id} r={r} />)
          )}
        </Card>
      </div>

      {/* 10. Actualités */}
      <SectionHeader icon={Newspaper} title="Actualités" testId="games-section-news" />
      <Card className="p-5">
        <EntityTimeline entityType="any" entityId="all" road2027 extraParams={{ all: 1 }} limit={40} showAthleteName />
      </Card>

      {/* 11. Histoire des JP — visuel de synthèse (V12.x) */}
      <SectionHeader icon={Flag} title="Histoire des Jeux du Pacifique &amp; parcours de Tahiti" hint="1963 → 2027" testId="games-section-history" />
      <Card className="p-2 sm:p-3 overflow-hidden bg-white">
        <img
          src="/branding/histoire-jp-visuel.png"
          alt="Histoire des Jeux du Pacifique et parcours de Tahiti de 1963 à 2027"
          className="w-full h-auto rounded"
          data-testid="road2027-histoire-jp-image"
        />
      </Card>
      <p className="mt-1 text-[11px] text-muted-foreground text-center italic">
        Repères clés, éditions, médailles d'or de Tahiti · sources : Pacific Games Council, COPF, Tahiti VOD, archives publiques.
        <span className="ml-1"><Link to="/memoire" className="underline hover:text-[hsl(var(--aito-lagoon))]">Voir la Mémoire du sport</Link></span>
      </p>

      <p className="mt-8 text-center text-[11px] text-muted-foreground italic">
        Sélection officielle validée par AITO / COPF. Les résultats des Jeux se synchronisent automatiquement avec les palmarès des athlètes AITO.PF.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
//  Sous-composants locaux
// ---------------------------------------------------------------------------
function SectionHeader({ icon: Icon, title, hint, testId }) {
  return (
    <div className="flex items-baseline justify-between flex-wrap gap-2 mt-8 mb-3" data-testid={testId}>
      <h2 className="font-display text-lg font-bold flex items-center gap-2">
        <Icon className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
        {title}
      </h2>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
    </div>
  );
}

function MiniStat({ label, value, tone = "default", testId }) {
  const valCls =
    tone === "or" ? "text-[hsl(var(--aito-gold))]"
    : tone === "argent" ? "text-slate-500 dark:text-slate-300"
    : tone === "bronze" ? "text-amber-700 dark:text-amber-500"
    : tone === "total" ? "text-[hsl(var(--aito-lagoon))]"
    : "text-foreground";
  return (
    <div className="flex flex-col" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className={`font-mono text-lg sm:text-xl font-bold leading-none ${valCls}`}>{value}</span>
    </div>
  );
}
